import { addressToScriptHash, AttributeUsageModel, common, InputModel, InvocationTransactionModel, OutputModel, utils, WitnessModel, } from '@neo-one/client-common';
import { processActionsAndMessage } from '@neo-one/client-switch';
import { Labels, utils as commonUtils } from '@neo-one/utils';
import BigNumber from 'bignumber.js';
import debug from 'debug';
import _ from 'lodash';
import { clientUtils } from '../clientUtils';
import { FundsInUseError, InsufficientFundsError, InsufficientSystemFeeError, InvalidTransactionError, InvokeError, NoAccountError, NothingToClaimError, NothingToRefundError, NothingToSendError, NotImplementedError, } from '../errors';
import { converters } from './converters';
const logger = debug('NEOONE:LocalUserAccountProvider');
const NEO_ONE_ATTRIBUTE = {
    usage: 'Remark15',
    data: Buffer.from('neo-one', 'utf8').toString('hex'),
};
export class UserAccountProviderBase {
    constructor({ provider }) {
        this.provider = provider;
        this.mutableBlockCount = 0;
        this.mutableUsedOutputs = new Set();
        const iterActionsRaw = this.provider.iterActionsRaw;
        if (iterActionsRaw !== undefined) {
            this.iterActionsRaw = iterActionsRaw.bind(this.provider);
        }
    }
    getCurrentUserAccount() {
        throw new NotImplementedError('getCurrentUserAccount');
    }
    getUserAccounts() {
        throw new NotImplementedError('getUserAccounts');
    }
    getNetworks() {
        throw new NotImplementedError('getNetworks');
    }
    async selectUserAccount(_id) {
        throw new NotImplementedError('selectUserAccount');
    }
    iterBlocks(network, options) {
        return this.provider.iterBlocks(network, options);
    }
    async getBlockCount(network) {
        return this.provider.getBlockCount(network);
    }
    async getAccount(network, address) {
        return this.provider.getAccount(network, address);
    }
    async transfer(transfers, options) {
        const { from, attributes, networkFee } = this.getTransactionOptions(options);
        return this.capture(async () => this.executeTransfer(transfers, from, attributes, networkFee), {
            name: 'neo_transfer',
        });
    }
    async claim(options) {
        const { from, attributes, networkFee } = this.getTransactionOptions(options);
        return this.capture(async () => this.executeClaim(from, attributes, networkFee), {
            name: 'neo_claim',
        });
    }
    async invoke(contract, method, params, paramsZipped, verify, options = {}, sourceMaps = {}) {
        const { attributes = [] } = options;
        const transactionOptions = this.getTransactionOptions(options);
        const { from } = transactionOptions;
        const send = options.sendFrom === undefined ? [] : options.sendFrom;
        const receive = options.sendTo === undefined ? [] : options.sendTo;
        const contractID = {
            address: contract,
            network: from.network,
        };
        return this.invokeRaw({
            invokeMethodOptionsOrScript: {
                contract,
                invokeMethod: method,
                params,
            },
            transfers: send
                .map((transfer) => ({ ...transfer, from: contractID }))
                .concat(receive.map((transfer) => ({ ...transfer, from, to: contract }))),
            options: {
                ...transactionOptions,
                attributes: attributes.concat(this.getInvokeAttributes(contract, method, paramsZipped, verify && options.sendFrom === undefined && options.sendTo !== undefined, options.sendTo === undefined && options.sendFrom !== undefined ? from.address : undefined)),
            },
            onConfirm: ({ receipt, data }) => ({
                blockIndex: receipt.blockIndex,
                blockHash: receipt.blockHash,
                transactionIndex: receipt.transactionIndex,
                globalIndex: receipt.globalIndex,
                result: data.result,
                actions: data.actions,
            }),
            scripts: this.getInvokeScripts(method, params, verify && (options.sendFrom !== undefined || options.sendTo !== undefined)),
            method: 'invoke',
            labels: {
                [Labels.INVOKE_METHOD]: method,
            },
            sourceMaps,
        });
    }
    async invokeSend(contract, method, paramsIn, paramsZipped, transfer, options = {}, sourceMaps = {}) {
        const transactionOptions = this.getTransactionOptions(options);
        const { from, attributes } = transactionOptions;
        const contractID = {
            address: contract,
            network: from.network,
        };
        const params = paramsIn.concat([common.stringToUInt160(addressToScriptHash(transfer.to))]);
        return this.invokeRaw({
            invokeMethodOptionsOrScript: {
                contract,
                invokeMethod: method,
                params,
            },
            options: {
                ...transactionOptions,
                attributes: attributes.concat(this.getInvokeAttributes(contract, method, paramsZipped, false, from.address)),
            },
            onConfirm: ({ receipt, data }) => ({
                blockIndex: receipt.blockIndex,
                blockHash: receipt.blockHash,
                transactionIndex: receipt.transactionIndex,
                globalIndex: receipt.globalIndex,
                result: data.result,
                actions: data.actions,
            }),
            scripts: this.getInvokeScripts(method, params, true),
            transfers: [
                {
                    ...transfer,
                    to: contract,
                    from: contractID,
                },
            ],
            reorderOutputs: (outputs) => {
                const output = outputs.find(({ value }) => value.isEqualTo(transfer.amount));
                if (output === undefined) {
                    throw new Error('Something went wrong.');
                }
                const outputIdx = outputs.indexOf(output);
                if (outputIdx === -1) {
                    throw new Error('Something went wrong.');
                }
                return [outputs[outputIdx]].concat(outputs.slice(0, outputIdx)).concat(outputs.slice(outputIdx + 1));
            },
            method: 'invokeSend',
            labels: {
                [Labels.INVOKE_METHOD]: method,
            },
            sourceMaps,
        });
    }
    async invokeCompleteSend(contract, method, params, paramsZipped, hash, options = {}, sourceMaps = {}) {
        const transactionOptions = this.getTransactionOptions(options);
        const { from, attributes } = transactionOptions;
        const sendTransaction = await this.provider.getTransaction(from.network, hash);
        if (sendTransaction.outputs.length === 0) {
            throw new NothingToSendError();
        }
        const sendInput = { hash, index: 0 };
        const sendOutput = {
            ...sendTransaction.outputs[0],
            address: from.address,
        };
        return this.invokeRaw({
            invokeMethodOptionsOrScript: {
                contract,
                invokeMethod: method,
                params,
            },
            options: {
                ...transactionOptions,
                attributes: attributes.concat(this.getInvokeAttributes(contract, method, paramsZipped, false, from.address)),
            },
            onConfirm: ({ receipt, data }) => ({
                blockIndex: receipt.blockIndex,
                blockHash: receipt.blockHash,
                transactionIndex: receipt.transactionIndex,
                globalIndex: receipt.globalIndex,
                result: data.result,
                actions: data.actions,
            }),
            scripts: this.getInvokeScripts(method, params, true),
            rawInputs: [sendInput],
            rawOutputs: [sendOutput],
            method: 'invokeCompleteSend',
            labels: {
                [Labels.INVOKE_METHOD]: method,
            },
            sourceMaps,
        });
    }
    async invokeRefundAssets(contract, method, params, paramsZipped, hash, options = {}, sourceMaps = {}) {
        const transactionOptions = this.getTransactionOptions(options);
        const { from, attributes } = transactionOptions;
        const refundTransaction = await this.provider.getTransaction(from.network, hash);
        const refundOutputs = refundTransaction.outputs
            .map((output, idx) => ({ output, idx }))
            .filter(({ output }) => output.address === contract);
        const inputs = refundOutputs.map(({ idx }) => ({ hash, index: idx }));
        const outputs = Object.entries(_.groupBy(refundOutputs.map(({ output }) => output), (output) => output.asset)).map(([asset, assetOutputs]) => ({
            address: from.address,
            asset,
            value: assetOutputs.reduce((acc, output) => acc.plus(output.value), new BigNumber('0')),
        }));
        if (inputs.length === 0) {
            throw new NothingToRefundError();
        }
        return this.invokeRaw({
            invokeMethodOptionsOrScript: {
                contract,
                invokeMethod: method,
                params,
            },
            options: {
                ...transactionOptions,
                attributes: attributes.concat(this.getInvokeAttributes(contract, method, paramsZipped, false, from.address)),
            },
            onConfirm: ({ receipt, data }) => ({
                blockIndex: receipt.blockIndex,
                blockHash: receipt.blockHash,
                transactionIndex: receipt.transactionIndex,
                globalIndex: receipt.globalIndex,
                result: data.result,
                actions: data.actions,
            }),
            scripts: this.getInvokeScripts(method, params, true),
            rawInputs: inputs,
            rawOutputs: outputs,
            method: 'invokeRefundAssets',
            labels: {
                [Labels.INVOKE_METHOD]: method,
            },
            sourceMaps,
        });
    }
    async invokeClaim(contract, method, params, paramsZipped, options = {}, sourceMaps = {}) {
        const { from, attributes, networkFee } = this.getTransactionOptions(options);
        return this.capture(async () => {
            const [{ unclaimed, amount }, { inputs, outputs }] = await Promise.all([
                this.provider.getUnclaimed(from.network, contract),
                this.getTransfersInputOutputs({
                    from,
                    gas: networkFee,
                    transfers: [],
                }),
            ]);
            if (unclaimed.length === 0) {
                throw new NothingToClaimError(from);
            }
            return this.executeInvokeClaim({
                contract,
                inputs,
                outputs,
                unclaimed,
                amount,
                attributes,
                from,
                method,
                params,
                paramsZipped,
                sourceMaps,
            });
        }, {
            name: 'neo_invoke_claim',
        });
    }
    async call(network, contract, method, params) {
        return this.provider.call(network, contract, method, params);
    }
    getTransactionOptions(options = {}) {
        const { attributes = [], networkFee = utils.ZERO_BIG_NUMBER, systemFee = utils.ZERO_BIG_NUMBER, skipSysFeeCheck = false, } = options;
        const { from: fromIn } = options;
        let from = fromIn;
        if (from === undefined) {
            const fromAccount = this.getCurrentUserAccount();
            if (fromAccount === undefined) {
                throw new NoAccountError();
            }
            from = fromAccount.id;
        }
        return {
            from,
            attributes: attributes.concat([NEO_ONE_ATTRIBUTE]),
            networkFee,
            systemFee,
            skipSysFeeCheck,
        };
    }
    getInvokeAttributes(contract, method, paramsZipped, verify, from) {
        return [
            {
                usage: 'Remark14',
                data: Buffer.from(`neo-one-invoke:${this.getInvokeAttributeTag(contract, method, paramsZipped)}`, 'utf8').toString('hex'),
            },
            verify
                ? {
                    usage: 'Script',
                    data: contract,
                }
                : undefined,
            from === undefined
                ? undefined
                : {
                    usage: 'Script',
                    data: from,
                },
        ].filter(commonUtils.notNull);
    }
    getInvokeScripts(method, params, verify) {
        return [
            verify
                ? new WitnessModel({
                    invocation: clientUtils.getInvokeMethodInvocationScript({
                        method,
                        params,
                    }),
                    verification: Buffer.alloc(0, 0),
                })
                : undefined,
        ].filter(commonUtils.notNull);
    }
    async consolidate({ inputs, transactionUnsignedIn, from, byteLimit, }) {
        const messageSize = transactionUnsignedIn.serializeUnsigned().length;
        const getMessageSize = ({ numNewInputs, numNewOutputs = 0, }) => messageSize + numNewInputs * InputModel.size + numNewOutputs * OutputModel.size;
        const { unspentOutputs: consolidatableUnspents } = await this.getUnspentOutputs({ from });
        const assetToInputOutputsUnsorted = consolidatableUnspents
            .filter((unspent) => !inputs.some((input) => unspent.hash === input.hash && unspent.index === input.index))
            .reduce((acc, unspent) => {
            if (acc[unspent.asset] !== undefined) {
                return {
                    ...acc,
                    [unspent.asset]: acc[unspent.asset].concat([unspent]),
                };
            }
            return {
                ...acc,
                [unspent.asset]: [unspent],
            };
        }, {});
        const assetToInputOutputs = Object.entries(assetToInputOutputsUnsorted).reduce((acc, [_asset, outputs]) => ({
            ...acc,
            [_asset]: outputs.slice().sort((coinA, coinB) => coinA.value.comparedTo(coinB.value)),
        }), assetToInputOutputsUnsorted);
        const { newInputs, updatedOutputs, remainingAssetToInputOutputs } = transactionUnsignedIn.outputs.reduce((acc, output) => {
            const asset = common.uInt256ToString(output.asset);
            const unspentOutputsIn = acc.remainingAssetToInputOutputs[asset];
            const unspentOutputs = unspentOutputsIn === undefined || common.uInt160ToString(output.address) !== addressToScriptHash(from.address)
                ? []
                : acc.remainingAssetToInputOutputs[asset];
            const tempIns = unspentOutputs.slice(0, Math.max(Math.floor((byteLimit - getMessageSize({ numNewInputs: acc.newInputs.length })) / InputModel.size), 0));
            return {
                newInputs: acc.newInputs.concat(this.convertInputs(tempIns)),
                updatedOutputs: acc.updatedOutputs.concat([
                    output.clone({
                        value: output.value.add(utils.bigNumberToBN(tempIns.reduce((left, right) => left.plus(right.value), new BigNumber('0')), 8)),
                    }),
                ]),
                remainingAssetToInputOutputs: unspentOutputsIn === undefined
                    ? acc.remainingAssetToInputOutputs
                    : {
                        ...acc.remainingAssetToInputOutputs,
                        [asset]: unspentOutputsIn.slice(tempIns.length),
                    },
            };
        }, {
            newInputs: [],
            updatedOutputs: [],
            remainingAssetToInputOutputs: assetToInputOutputs,
        });
        const { finalInputs, newOutputs } = _.sortBy(Object.entries(remainingAssetToInputOutputs).filter(([_asset, outputs]) => outputs.length >= 2), ([asset]) => {
            if (asset === common.NEO_ASSET_HASH) {
                return 0;
            }
            if (asset === common.GAS_ASSET_HASH) {
                return 1;
            }
            return 2;
        }).reduce((acc, [asset, outputs]) => {
            const newUnspents = outputs.slice(0, Math.max(0, Math.floor((byteLimit -
                getMessageSize({ numNewInputs: acc.finalInputs.length, numNewOutputs: acc.newOutputs.length }) -
                OutputModel.size) /
                InputModel.size)));
            return {
                finalInputs: acc.finalInputs.concat(this.convertInputs(newUnspents)),
                newOutputs: newUnspents.length === 0
                    ? acc.newOutputs
                    : acc.newOutputs.concat(this.convertOutputs([
                        {
                            asset,
                            value: newUnspents.reduce((left, right) => left.plus(right.value), new BigNumber('0')),
                            address: from.address,
                        },
                    ])),
            };
        }, { finalInputs: newInputs, newOutputs: [] });
        return transactionUnsignedIn.clone({
            inputs: transactionUnsignedIn.inputs.concat(finalInputs),
            outputs: updatedOutputs.concat(newOutputs),
        });
    }
    addWitness({ transaction, inputOutputs, address, witness, }) {
        const scriptAttributes = transaction.attributes.filter((attribute) => attribute.usage === AttributeUsageModel.Script);
        const scriptHash = addressToScriptHash(address);
        const scriptHashes = [
            ...new Set(inputOutputs
                .map((inputOutput) => addressToScriptHash(inputOutput.address))
                .concat(scriptAttributes.map((attribute) => common.uInt160ToString(attribute.value)))),
        ].filter((otherHash) => otherHash !== scriptHash);
        if (scriptHashes.length === 1) {
            if (transaction.scripts.length !== 1) {
                throw new InvalidTransactionError('Something went wrong! Expected 1 script.');
            }
            const otherHash = scriptHashes[0];
            const otherScript = transaction.scripts[0];
            return transaction.clone({
                scripts: _.sortBy([
                    [scriptHash, witness],
                    [otherHash, otherScript],
                ], [(value) => value[0]]).map((value) => value[1]),
            });
        }
        if (scriptHashes.length === 0) {
            if (transaction.scripts.length !== 0) {
                throw new InvalidTransactionError('Something went wrong! Expected 0 scripts.');
            }
            return transaction.clone({ scripts: [witness] });
        }
        throw new InvalidTransactionError('Something went wrong!');
    }
    async getUnspentOutputs({ from, }) {
        const [newBlockCount, allUnspentsIn] = await Promise.all([
            this.provider.getBlockCount(from.network),
            this.provider.getUnspentOutputs(from.network, from.address),
        ]);
        if (newBlockCount !== this.mutableBlockCount) {
            this.mutableUsedOutputs.clear();
            this.mutableBlockCount = newBlockCount;
        }
        const unspentOutputs = allUnspentsIn.filter((unspent) => !this.mutableUsedOutputs.has(`${unspent.hash}:${unspent.index}`));
        const wasFiltered = allUnspentsIn.length !== unspentOutputs.length;
        return { unspentOutputs, wasFiltered };
    }
    async getTransfersInputOutputs({ transfers, from, gas, }) {
        if (transfers.length === 0 && gas.lte(utils.ZERO_BIG_NUMBER)) {
            return { inputs: [], outputs: [] };
        }
        const mutableGroupedTransfers = _.groupBy(transfers, ({ from: transferFrom }) => transferFrom.address);
        if (gas.isGreaterThan(utils.ZERO_BIG_NUMBER)) {
            const fromTransfers = mutableGroupedTransfers[from.address];
            const newTransfer = {
                from,
                amount: gas,
                asset: common.GAS_ASSET_HASH,
            };
            mutableGroupedTransfers[from.address] =
                fromTransfers === undefined ? [newTransfer] : fromTransfers.concat([newTransfer]);
        }
        const results = await Promise.all(Object.values(mutableGroupedTransfers).map(async (transfersFrom) => this.getTransfersInputOutputsFrom({
            transfers: transfersFrom,
            from: transfersFrom[0].from,
        })));
        return results.reduce((acc, { outputs, inputs }) => ({
            outputs: acc.outputs.concat(outputs),
            inputs: acc.inputs.concat(inputs),
        }), { outputs: [], inputs: [] });
    }
    async getTransfersInputOutputsFrom({ transfers, from, }) {
        const { unspentOutputs: allOutputs, wasFiltered } = await this.getUnspentOutputs({ from });
        return Object.values(_.groupBy(transfers, ({ asset }) => asset)).reduce((acc, toByAsset) => {
            const { asset } = toByAsset[0];
            const assetResults = toByAsset.reduce(({ remaining, remainingOutputs, inputs, outputs: innerOutputs }, { amount, to }) => {
                const result = this.getTransferInputOutputs({
                    from: from.address,
                    to,
                    asset,
                    amount,
                    remainingOutputs,
                    remaining,
                    wasFiltered,
                });
                return {
                    remaining: result.remaining,
                    remainingOutputs: result.remainingOutputs,
                    inputs: inputs.concat(result.inputs),
                    outputs: innerOutputs.concat(result.outputs),
                };
            }, {
                remaining: utils.ZERO_BIG_NUMBER,
                remainingOutputs: allOutputs.filter((output) => output.asset === asset),
                inputs: [],
                outputs: [],
            });
            let outputs = acc.outputs.concat(assetResults.outputs);
            if (assetResults.remaining.gt(utils.ZERO_BIG_NUMBER)) {
                outputs = outputs.concat([
                    {
                        address: from.address,
                        asset,
                        value: assetResults.remaining,
                    },
                ]);
            }
            return {
                inputs: acc.inputs.concat(assetResults.inputs),
                outputs,
            };
        }, { inputs: [], outputs: [] });
    }
    getTransferInputOutputs({ to, amount: originalAmount, asset, remainingOutputs, remaining, wasFiltered, }) {
        const amount = originalAmount.minus(remaining);
        const outputs = to === undefined
            ? []
            : [
                {
                    address: to,
                    asset,
                    value: originalAmount,
                },
            ];
        if (amount.lte(utils.ZERO_BIG_NUMBER)) {
            return {
                inputs: [],
                outputs,
                remainingOutputs,
                remaining: remaining.minus(originalAmount),
            };
        }
        const outputsOrdered = remainingOutputs.slice().sort((coinA, coinB) => coinA.value.comparedTo(coinB.value) * -1);
        const sum = outputsOrdered.reduce((acc, coin) => acc.plus(coin.value), utils.ZERO_BIG_NUMBER);
        if (sum.lt(amount)) {
            if (wasFiltered) {
                throw new FundsInUseError(sum, amount, this.mutableUsedOutputs.size);
            }
            throw new InsufficientFundsError(sum, amount);
        }
        let k = 0;
        let amountRemaining = amount.plus(utils.ZERO_BIG_NUMBER);
        while (outputsOrdered[k].value.lte(amountRemaining)) {
            amountRemaining = amountRemaining.minus(outputsOrdered[k].value);
            if (amountRemaining.isEqualTo(utils.ZERO_BIG_NUMBER)) {
                break;
            }
            k += 1;
        }
        let coinAmount = utils.ZERO_BIG_NUMBER;
        const mutableInputs = [];
        for (let i = 0; i < k + 1; i += 1) {
            coinAmount = coinAmount.plus(outputsOrdered[i].value);
            mutableInputs.push(outputsOrdered[i]);
        }
        return {
            inputs: mutableInputs,
            outputs,
            remainingOutputs: outputsOrdered.slice(k + 1),
            remaining: coinAmount.minus(amount),
        };
    }
    getInvokeAttributeTag(contract, method, paramsZipped) {
        return JSON.stringify({
            contract,
            method,
            params: paramsZipped.map(([name, param]) => [name, this.paramToJSON(param)]),
        });
    }
    paramToJSON(param) {
        if (param === undefined) {
            return param;
        }
        if (Array.isArray(param)) {
            return param.map((value) => this.paramToJSON(value));
        }
        if (BigNumber.isBigNumber(param) || param instanceof BigNumber) {
            return param.toString();
        }
        if (typeof param === 'object') {
            return this.paramToJSON(param.param);
        }
        return param;
    }
    convertAttributes(attributes) {
        return attributes.map((attribute) => converters.attribute(attribute));
    }
    convertInputs(inputs) {
        return inputs.map((input) => converters.input(input));
    }
    convertOutputs(outputs) {
        return outputs.map((output) => converters.output(output));
    }
    convertWitness(script) {
        return converters.witness(script);
    }
    async capture(func, { name, labels = {}, invoke = false, }) {
        if (invoke) {
            const value = labels[Labels.INVOKE_RAW_METHOD];
            if (value === undefined) {
                throw new Error('invocation should have a invoke method');
            }
        }
        try {
            const result = await func();
            logger('%o', { name, level: 'verbose', ...labels });
            return result;
        }
        catch (error) {
            logger('%o', { name, level: 'error', error: error.message, ...labels });
            throw error;
        }
    }
    async checkSystemFees({ script, transfers = [], from, networkFee, systemFee, attributes: attributesIn = [], rawInputs = [], rawOutputs = [], scripts = [], sourceMaps, reorderOutputs = (outputs) => outputs, skipSysFeeCheck = false, }) {
        const { inputs: testInputs, outputs: testOutputs } = await this.getTransfersInputOutputs({
            transfers,
            from,
            gas: networkFee,
        });
        const attributes = attributesIn.concat({
            usage: 'Remark15',
            data: Buffer.from(`${utils.randomUInt()}`, 'utf8').toString('hex'),
        });
        if (skipSysFeeCheck) {
            return { gas: utils.ZERO_BIG_NUMBER, attributes };
        }
        const testTransaction = new InvocationTransactionModel({
            version: 1,
            inputs: this.convertInputs(rawInputs.concat(testInputs)),
            outputs: this.convertOutputs(reorderOutputs(rawOutputs.concat(testOutputs))),
            attributes: this.convertAttributes(attributes.concat([
                {
                    usage: 'Script',
                    data: from.address,
                },
            ])),
            gas: common.TEN_THOUSAND_FIXED8,
            script,
            scripts,
        });
        const callReceipt = await this.provider.testInvoke(from.network, testTransaction);
        if (callReceipt.result.state === 'FAULT') {
            const message = await processActionsAndMessage({
                actions: callReceipt.actions,
                message: callReceipt.result.message,
                sourceMaps,
            });
            throw new InvokeError(message);
        }
        const gas = callReceipt.result.gasConsumed.integerValue(BigNumber.ROUND_UP);
        if (gas.gt(utils.ZERO_BIG_NUMBER) && systemFee.lt(gas) && !systemFee.eq(utils.NEGATIVE_ONE_BIG_NUMBER)) {
            throw new InsufficientSystemFeeError(systemFee, gas);
        }
        return { gas, attributes };
    }
    async invokeRaw({ invokeMethodOptionsOrScript, transfers = [], options = {}, onConfirm, method, verify = true, scripts = [], labels = {}, rawInputs = [], rawOutputs = [], sourceMaps, reorderOutputs = (outputs) => outputs, }) {
        const { from, attributes: attributesIn, networkFee, systemFee, skipSysFeeCheck } = this.getTransactionOptions(options);
        const { script, invokeMethodOptions } = this.getScriptAndInvokeMethodOptions(invokeMethodOptionsOrScript);
        return this.capture(async () => {
            const { gas, attributes } = await this.checkSystemFees({
                script,
                transfers,
                from,
                networkFee,
                systemFee,
                attributes: attributesIn,
                rawInputs,
                rawOutputs,
                scripts,
                sourceMaps,
                reorderOutputs,
                skipSysFeeCheck,
            });
            const { inputs, outputs } = await this.getTransfersInputOutputs({
                transfers,
                from,
                gas: networkFee.plus(gas),
            });
            return invokeMethodOptions === undefined
                ? this.executeInvokeScript({
                    script,
                    from,
                    attributes,
                    inputs,
                    outputs,
                    rawInputs,
                    rawOutputs,
                    gas,
                    verify,
                    scripts,
                    reorderOutputs,
                    onConfirm,
                    sourceMaps,
                })
                : this.executeInvokeMethod({
                    script,
                    invokeMethodOptions,
                    from,
                    attributes,
                    inputs,
                    outputs,
                    rawInputs,
                    rawOutputs,
                    gas,
                    verify,
                    scripts,
                    reorderOutputs,
                    onConfirm,
                    sourceMaps,
                });
        }, {
            name: 'neo_invoke_raw',
            invoke: true,
            labels: {
                [Labels.INVOKE_RAW_METHOD]: method,
                ...labels,
            },
        });
    }
    getScriptAndInvokeMethodOptions(invokeMethodOptionsOrScript) {
        let script;
        let invokeMethodOptions;
        if (invokeMethodOptionsOrScript instanceof Buffer) {
            script = invokeMethodOptionsOrScript;
        }
        else {
            invokeMethodOptions = invokeMethodOptionsOrScript;
            const { contract, invokeMethod, params } = invokeMethodOptions;
            script = clientUtils.getInvokeMethodScript({
                address: contract,
                method: invokeMethod,
                params,
            });
        }
        return { script, invokeMethodOptions };
    }
}

//# sourceMappingURL=UserAccountProviderBase.js.map
